import { defineStore } from 'pinia'
import vacancyImporter from '../api/vacancyImporter'

const emptyconfig = {
  ats: '',
  targetSites: [],
  cutoffDate: null,
  customization: [],
  archived: false,
  triggerType: 'MANUAL',
  sourceOfTruth: 'ATS',
  cronSchedule: '0 */30 * * * *',
}

const availableTriggerTypes = {
  recruitee: [
    { label: 'Manual', value: 'MANUAL' },
    { label: 'Schedule', value: 'SCHEDULE' },
    { label: 'Webhook', value: 'WEBHOOK' },
  ],

  connexys: [
    { label: 'Manual', value: 'MANUAL' },
    { label: 'Schedule', value: 'SCHEDULE' },
  ],

  'solutions-factory': [
    { label: 'Manual', value: 'MANUAL' },
    { label: 'Schedule', value: 'SCHEDULE' },
  ],

  ubeeo: [
    { label: 'Manual', value: 'MANUAL' },
    { label: 'Schedule', value: 'SCHEDULE' },
  ],
}

const availableSourceOfTruth = [
  { label: 'From ATS', value: 'ATS' },
  { label: 'From Publisher', value: 'JOBSREPUBLIC' },
]

const availableIntervals = [
  { label: 'Every 15 mins', value: '0 */15 * * * *' },
  { label: 'Every 30 mins', value: '0 */30 * * * *' },
  { label: 'Every 60 mins', value: '0 */60 * * * *' },
]
export const useImporterStore = defineStore('importerStore', {
  state: () => ({
    isLoading: false,
    isSaving: false,
    error: null,
    isSavedSuccessfully: false,
    configs: [],
    activeConfig: JSON.parse(JSON.stringify(emptyconfig)), // we make sure to use a new object, not a reference,
    events: [],
    selectedEvent: null,
    availableTriggerTypes,
    availableIntervals: availableIntervals,
    availableSourceOfTruth: availableSourceOfTruth,
    availableATS: [
      { label: 'Connexys', value: 'connexys' },
      { label: 'Recruitee', value: 'recruitee' },
      { label: 'Solutions Factory', value: 'solutions-factory' },
      { label: 'Ubeeo', value: 'ubeeo' },
    ],
    filter: {
      companyIds: [],
      statuses: [],
      atsNames: [],
    },
    dateRangeFilter: [null, null],
    sortBy: 'id',
    sortOrder: 'desc',
    page: {
      number: 1,
      size: 10,
      totalElements: 0,
      totalPages: 0,
    },
  }),
  actions: {
    async loadConfig(companyId, configId, ats = '') {
      this.configs = []
      this.activeConfig = JSON.parse(JSON.stringify(emptyconfig))
      this.error = null
      this.selectedEvent = null

      const params = {
        ats,
        page: this.page.number,
        size: this.page.size,
        sortBy: this.sortBy,
        sortOrder: this.sortOrder,
      }
      if (companyId) {
        params.companyId = companyId
        params.page = 1
        params.size = 100
      }

      this.isLoading = true
      try {
        const { data, meta } =
          await vacancyImporter.getVacancyImportConfiguration(params)
        if (data?.length) {
          this.configs = data
          this.page = meta.page

          this.activeConfig = data[0]
          if (this.configs.length > 1) {
            const desiredConfig = this.configs.find(
              // Here we use == on purpose because they id may come as a string
              (conf) => conf.id == configId,
            )
            if (desiredConfig) {
              this.activeConfig = desiredConfig
            }
          }
        } else {
          this.activeConfig = emptyconfig
        }
      } catch (error) {
        this.error = error
      } finally {
        this.isLoading = false
      }
    },
    async saveConfig(companyId) {
      try {
        this.isSaving = true
        this.error = null
        this.isSavedSuccessfully = false

        const targetSites = this.activeConfig.targetSites.map((site) => site.id)
        const configToSend = {
          ...this.activeConfig,
          targetSites,
          publisherId: this.activeConfig.publisher.id,
        }
        if (configToSend.triggerType !== 'SCHEDULE') {
          configToSend.cronSchedule = undefined
        }

        if (this.activeConfig.id) {
          await vacancyImporter.putVacancyImportConfiguration(configToSend)
        } else {
          const savedConfig =
            await vacancyImporter.postVacancyImportConfiguration({
              companyId,
              ...configToSend,
            })

          this.activeConfig.id = savedConfig.data.id
          this.configs[this.configs.length - 1] = this.activeConfig
        }
        this.isSavedSuccessfully = true
      } catch (error) {
        this.error = error
      } finally {
        this.isSaving = false
      }
    },
    changeActiveConfig(index) {
      this.activeConfig = this.configs[index]
      this.error = null
      this.selectedEvent = null
      this.isSavedSuccessfully = false
    },
    async executeImport(companyId) {
      try {
        this.isLoading = true
        if (!this.activeConfig.id) {
          return
        }

        const { data } = await vacancyImporter.postImport({
          companyId,
          ats: this.activeConfig.ats,
        })
        this.selectedEvent = this.processEvent(data)
      } catch (error) {
        this.error = error
      } finally {
        this.isLoading = false
      }
    },
    async loadEvents(companyId) {
      try {
        this.isLoading = true
        this.events = []
        this.selectedEvent = null

        if (!companyId) {
          return
        }

        const { data, meta } = await vacancyImporter.getImports({
          companyId,
          page: this.page.number,
          size: this.page.size,
          sortBy: 'dateCreated',
          sortOrder: 'desc',
        })

        this.events = data.map((event) => this.processEvent(event))
        this.page = meta.page
      } catch (error) {
        this.error = error
      } finally {
        this.isLoading = false
      }
    },
    async loadAllEvents() {
      try {
        this.isLoading = true
        this.events = []
        this.selectedEvent = null

        const filter = { ...this.filter }
        if (this.filter.companyIds.length) {
          filter.companyIds = this.filter.companyIds.map(
            (company) => company.id,
          )
        }
        if (this.dateRangeFilter[0] && this.dateRangeFilter[1]) {
          filter.dateCreated = {
            start: new Date(this.dateRangeFilter[0]).toISOString(),
            end: new Date(this.dateRangeFilter[1]).toISOString(),
          }
        }

        const { data, meta } = await vacancyImporter.searchImports(filter, {
          page: this.page.number,
          size: this.page.size,
          sortBy: 'dateCreated',
          sortOrder: 'desc',
        })

        this.events = data.map((event) => this.processEvent(event))
        this.page = meta.page
      } catch (error) {
        this.error = error
      } finally {
        this.isLoading = false
      }
    },
    async loadEvent(eventId) {
      try {
        this.isLoading = true
        this.selectedEvent = null
        if (!eventId) {
          return
        }

        const { data } = await vacancyImporter.getImport(eventId)
        this.selectedEvent = this.processEvent(data)
      } catch (error) {
        this.error = error
      } finally {
        this.isLoading = false
      }
    },
    processEvent(event) {
      // Group vacancies
      const created = []
      const updated = []
      const archived = []
      const unchanged = []
      for (const vacancyEvent of event.vacancyEvents) {
        if (vacancyEvent.type === 'CREATED') {
          created.push(vacancyEvent)
        } else if (vacancyEvent.type === 'UPDATED') {
          updated.push(vacancyEvent)
        } else if (vacancyEvent.type === 'CLOSED') {
          archived.push(vacancyEvent)
        } else if (vacancyEvent.type === 'UNCHANGED') {
          unchanged.push(vacancyEvent)
        }
      }

      return {
        ...event,
        vacancyEvents: { created, updated, archived, unchanged },
      }
    },
    addConfig() {
      const lastConfig = this.configs[this.configs.length - 1]
      // You can only have one unsaved config at a time
      if (lastConfig.id) {
        const emptyConfig = JSON.parse(JSON.stringify(emptyconfig))
        this.activeConfig = emptyConfig
        this.configs.push(emptyConfig)
        this.error = null
        this.selectedEvent = null
        this.isSavedSuccessfully = false
      }
    },
    resetConfig() {
      this.activeConfig = JSON.parse(JSON.stringify(emptyconfig))
    },
    async testAtsConfiguration(companyId) {
      try {
        this.isLoading = true
        this.error = null
        this.selectedEvent = null

        const targetSites = this.activeConfig.targetSites.map((site) => site.id)
        const configToSend = {
          ...this.activeConfig,
          targetSites,
          publisherId: this.activeConfig.publisher.id,
        }
        if (configToSend.triggerType !== 'SCHEDULE') {
          configToSend.cronSchedule = undefined
        }

        const { data } = await vacancyImporter.postDryImport({
          companyId,
          ...configToSend,
        })
        this.selectedEvent = {
          ...this.processEvent(data),
          mock: true,
        }
        if (this.selectedEvent.error) {
          this.error = this.selectedEvent.error
        }
      } catch (error) {
        this.error = error
      } finally {
        this.isLoading = false
      }
    },
  },
})
